import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Database, ArrowRight, CheckCircle2, AlertTriangle, Users2, FolderInput } from "lucide-react";

export default function Migration() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [heads, setHeads] = useState([]);
  const [existingPayees, setExistingPayees] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [preview, setPreview] = useState(null);
  const [typeMap, setTypeMap] = useState({});      // type_name -> head_id
  const [payeeMap, setPayeeMap] = useState({});    // dedup_key -> "__NEW__" | existing_id
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/projects").then(r => setProjects(r.data));
    api.get("/masters/heads").then(r => setHeads(r.data));
    api.get("/masters/payees").then(r => setExistingPayees(r.data));
  }, []);

  const doPreview = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/migration/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      setResult(null);
      // Init maps with auto-suggestions
      const tm = {};
      (r.data.distinct_types || []).forEach(t => { tm[t.name] = t.suggested_head_id || ""; });
      setTypeMap(tm);
      const pm = {};
      (r.data.distinct_payees || []).forEach(p => { pm[p.key] = p.matched_payee_id || "__NEW__"; });
      setPayeeMap(pm);
      toast.success(`Parsed ${r.data.rows_count} rows`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doCommit = async () => {
    if (!selectedProject) { toast.error("Pick a project first"); return; }
    // validate all types mapped
    for (const t of preview.distinct_types) {
      if (!typeMap[t.name]) { toast.error(`Map type "${t.name}" to a head first`); return; }
    }
    setCommitting(true);
    try {
      const body = {
        batch_id: preview.batch_id,
        project_id: selectedProject,
        type_to_head: typeMap,
        payee_choices: payeeMap,
      };
      const r = await api.post("/migration/commit", body);
      setResult(r.data);
      toast.success(`Migrated ${r.data.expenses_created} expenses`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  const isAdmin = user?.role === "Admin" || user?.role === "Accounts";

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Administration · Migration</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">Legacy Data Migration</h1>
        <p className="text-sm text-slate-500 mt-1">Import legacy expense/vendor CSVs with Type → Head mapping and PAN-based payee dedup.</p>
      </div>

      {!isAdmin && (
        <Card className="border-amber-200 bg-amber-50 mb-4">
          <CardContent className="p-4 text-sm text-amber-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Migration is limited to Admin and Accounts users.
          </CardContent>
        </Card>
      )}

      {isAdmin && (
        <>
          <Card className="mb-6">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 flex-wrap">
                <input type="file" ref={fileRef} accept=".csv" onChange={doPreview} className="hidden" data-testid="migration-file-input" />
                <Button onClick={() => fileRef.current?.click()} className="bg-[#0F172A]" data-testid="migration-upload-btn">
                  <Upload className="w-4 h-4 mr-1" /> Upload Legacy CSV
                </Button>
                <div className="text-xs text-slate-500 flex-1 min-w-[200px]">
                  Columns: <span className="font-mono">date, payee_name, pan, type, sub_type, amount, invoice_number, description</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {preview && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
                <Stat label="Rows Parsed" value={preview.rows_count} />
                <Stat label="Distinct Types" value={preview.distinct_types.length} />
                <Stat label="Distinct Payees" value={preview.distinct_payees.length} />
                <Stat label="Existing Payees Matched" value={preview.distinct_payees.filter(p => p.matched_payee_id).length} tone="emerald" />
              </div>

              {/* Type → Head mapping */}
              <Card className="mb-6">
                <CardContent className="p-5">
                  <div className="font-semibold mb-3 flex items-center gap-2"><FolderInput className="w-4 h-4" /> Type → Budget Head Mapping</div>
                  {preview.distinct_types.length === 0 && <div className="text-sm text-slate-500">No types found in CSV.</div>}
                  <div className="space-y-2">
                    {preview.distinct_types.map(t => (
                      <div key={t.name} className="grid grid-cols-1 md:grid-cols-[1fr_auto_2fr] items-center gap-3" data-testid={`type-map-${t.name}`}>
                        <div>
                          <div className="font-medium text-sm">{t.name}</div>
                          <div className="text-xs text-slate-500">{t.count} row{t.count !== 1 ? "s" : ""} · ₹ {t.total.toLocaleString()}</div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-slate-400 hidden md:inline" />
                        <Select value={typeMap[t.name] || ""} onValueChange={(v) => setTypeMap({ ...typeMap, [t.name]: v })}>
                          <SelectTrigger data-testid={`type-select-${t.name}`}><SelectValue placeholder="Select head…" /></SelectTrigger>
                          <SelectContent>{heads.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Payee dedup */}
              <Card className="mb-6">
                <CardContent className="p-5">
                  <div className="font-semibold mb-3 flex items-center gap-2"><Users2 className="w-4 h-4" /> Payee De-Duplication (by PAN)</div>
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="text-xs uppercase text-slate-500 bg-slate-50 sticky top-0">
                        <tr className="border-b"><th className="text-left px-3 py-2">Legacy Name</th><th className="text-left px-3 py-2">PAN</th><th className="text-right px-3 py-2">Occurrences</th><th className="text-left px-3 py-2 w-72">Choice</th></tr>
                      </thead>
                      <tbody>
                        {preview.distinct_payees.map(p => (
                          <tr key={p.key} className="border-b border-slate-100" data-testid={`payee-row-${p.key}`}>
                            <td className="px-3 py-2 font-medium">{p.name}</td>
                            <td className="px-3 py-2 font-mono text-xs">{p.pan || <span className="text-slate-400">—</span>}</td>
                            <td className="px-3 py-2 text-right font-mono">{p.count}</td>
                            <td className="px-3 py-2">
                              <Select value={payeeMap[p.key] || "__NEW__"} onValueChange={(v) => setPayeeMap({ ...payeeMap, [p.key]: v })}>
                                <SelectTrigger data-testid={`payee-select-${p.key}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__NEW__">➕ Create new payee</SelectItem>
                                  {p.matched_payee_id && <SelectItem value={p.matched_payee_id}>✓ Match to: {p.matched_payee_name}</SelectItem>}
                                  {existingPayees.filter(ep => ep.id !== p.matched_payee_id).map(ep => (
                                    <SelectItem key={ep.id} value={ep.id}>{ep.name}{ep.pan ? ` (${ep.pan})` : ""}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Commit */}
              <Card className="mb-6">
                <CardContent className="p-5 grid grid-cols-1 md:grid-cols-[1fr_auto] items-end gap-4">
                  <div>
                    <Label>Target Project (all imported rows land under this project)</Label>
                    <Select value={selectedProject} onValueChange={setSelectedProject}>
                      <SelectTrigger data-testid="migration-project-select"><SelectValue placeholder="Pick a project…" /></SelectTrigger>
                      <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Button onClick={doCommit} disabled={committing || !selectedProject} className="bg-emerald-700 hover:bg-emerald-800" data-testid="migration-commit-btn">
                    <Database className="w-4 h-4 mr-1" /> {committing ? "Committing…" : "Commit Migration"}
                  </Button>
                </CardContent>
              </Card>
            </>
          )}

          {result && (
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 font-semibold text-emerald-900"><CheckCircle2 className="w-5 h-5" /> Migration committed</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 text-sm">
                  <div><div className="text-slate-600">Payees created</div><div className="font-display font-bold text-2xl">{result.payees_created}</div></div>
                  <div><div className="text-slate-600">Payees reused</div><div className="font-display font-bold text-2xl">{result.payees_reused}</div></div>
                  <div><div className="text-slate-600">Expenses inserted</div><div className="font-display font-bold text-2xl">{result.expenses_created}</div></div>
                </div>
                {result.errors?.length > 0 && (
                  <div className="mt-4 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                    <div className="font-semibold mb-1">{result.errors.length} row{result.errors.length !== 1 ? "s" : ""} skipped:</div>
                    <ul className="list-disc list-inside space-y-0.5">
                      {result.errors.slice(0, 10).map((e, i) => <li key={i}><span className="font-mono">Row {e.row}:</span> {e.reason}</li>)}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = tone === "emerald" ? "text-emerald-700" : "text-slate-900";
  return (
    <Card><CardContent className="p-5">
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className={`font-display font-bold text-2xl mt-1 ${cls}`}>{value}</div>
    </CardContent></Card>
  );
}
